import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { FormsList } from '../../features/forms/forms-list';
import { archiveBlockedBy, describeArchiveBlock, isDuplicateKey } from '../../lib/api/forms';
import { useFormsClient } from '../../lib/api/forms-client-context';
import { describeApiError } from '../../lib/session/describe-error';

export const Route = createFileRoute('/forms/')({
  component: FormsPage,
});

/**
 * `/forms` — the intake form list (MASTER_PLAN M8.5, Docs/API.md §2.8).
 *
 * The route is the only thing here that knows about the network: it fetches, it mutates,
 * and it hands `<FormsList>` plain data and callbacks. Swapping the mock client for the
 * real one in M8.2 changes nothing below this file (rules §17.1).
 */
function FormsPage() {
  const client = useFormsClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createError, setCreateError] = useState<string | undefined>(undefined);
  const [createErrorField, setCreateErrorField] = useState<'key' | null>(null);
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined);

  const forms = useQuery({ queryKey: ['intake-forms'], queryFn: () => client.list() });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['intake-forms'] });
  };

  const create = useMutation({
    mutationFn: (input: { key: string; title: string }) => client.create(input),
    onSuccess: async (form) => {
      setCreateError(undefined);
      setCreateErrorField(null);
      toast({ tone: 'success', message: `“${form.title}” created as a draft.` });
      await refresh();
    },
    onError: (error: Error) => {
      /* `409 DUPLICATE` is always about the key, so the sentence goes under that box
         rather than at the top of a dialog with two fields in it. */
      setCreateErrorField(isDuplicateKey(error) ? 'key' : null);
      setCreateError(describeApiError(error));
    },
  });

  const duplicate = useMutation({
    mutationFn: (formId: string) => client.duplicate(formId),
    onSuccess: async (form) => {
      toast({ tone: 'success', message: `Copied to “${form.title}”.` });
      await refresh();
    },
  });

  const archive = useMutation({
    mutationFn: (formId: string) => client.archive(formId),
    onSuccess: async (form) => {
      setArchiveError(undefined);
      toast({ tone: 'success', message: `“${form.title}” archived.` });
      await refresh();
    },
    onError: (error: Error) => {
      /*
       * `409 CONFLICT` carries the visit types still pointing at this form. Naming them
       * is the difference between a refusal somebody can act on and one they can only
       * repeat: the fix is to point those visit types at another form first.
       */
      const blocking = archiveBlockedBy(error);
      const detail = blocking === null ? null : describeArchiveBlock(blocking);
      setArchiveError(
        detail === null ? describeApiError(error) : `${describeApiError(error)} ${detail}`,
      );
    },
  });

  if (forms.isPending) {
    return (
      <>
        <PageHeader title="Intake forms" />
        <Card className="p-6">
          <Spinner label="Loading forms" />
        </Card>
      </>
    );
  }

  if (forms.isError) {
    return (
      <>
        <PageHeader title="Intake forms" />
        <Alert tone="error">The forms could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <FormsList
      forms={forms.data}
      createError={createError}
      createErrorField={createErrorField}
      archiveError={archiveError}
      onCreate={(input) => {
        create.mutate(input);
      }}
      onDuplicate={(formId) => {
        duplicate.mutate(formId);
      }}
      onArchive={(formId) => {
        archive.mutate(formId);
      }}
      renderLink={(form, children) => (
        <Link
          to="/forms/$formId"
          params={{ formId: form.id }}
          className="font-medium text-primary-accessible hover:underline"
        >
          {children}
        </Link>
      )}
    />
  );
}
